import React, { useState } from "react";
import { Plus, Check, Play, Settings, RefreshCw, Activity, ArrowRight, Layers, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { AddWatchWizard } from "@/components/dashboard/AddWatchWizard";
import { IntegrationsCanvas } from "@/components/dashboard/IntegrationsCanvas";

// Mock Sparkline generator for visual feedback
function Sparkline() {
  return (
    <div className="flex items-end gap-0.5 h-6 w-24">
      {[4, 5, 4, 6, 8, 5, 4, 3, 5, 8, 9, 6, 4, 5, 7, 9, 9, 8, 9, 9].map((val, idx) => (
        <span
          key={idx}
          style={{ height: `${val * 10}%` }}
          className="flex-1 bg-green-500 rounded-none"
        />
      ))}
    </div>
  );
}

export default function Dashboard() {
  const queryClient = useQueryClient();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"dashboard" | "flow">("dashboard");

  // Fetch real sites (mapped to Watches)
  const { data: sites = [], isLoading } = useQuery({
    queryKey: ["websites"],
    queryFn: () => api.getSites(),
  });

  const createWatchMutation = useMutation({
    mutationFn: (newWatch: any) => api.createSite(newWatch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["websites"] });
    },
  });

  const togglePauseMutation = useMutation({
    mutationFn: async ({ id, paused }: { id: string; paused: boolean }) => {
      // Toggle pause status on database
      return api.updateSite(id, { is_active: !paused });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["websites"] });
    },
  });

  const handleCreated = (watchData: any) => {
    createWatchMutation.mutate(watchData);
  };

  // Premade example card if list is empty
  const exampleCard = {
    id: "example",
    name: "example.com",
    url: "https://example.com",
    status: "up",
    uptime_percentage: 99.99,
    last_ping_at: new Date().toISOString(),
    is_active: true,
  };

  const isAnyDown = sites.some((s: any) => s.status === "down");

  return (
    <div className="space-y-8 max-w-5xl mx-auto font-sans select-none pb-12">
      {/* Header Info */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 border-b-4 border-foreground pb-6">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className={`h-3.5 w-3.5 rounded-none ${isAnyDown ? "bg-red-500 animate-pulse" : "bg-green-500"}`} />
            <h1 className="text-2xl font-black uppercase tracking-tight text-foreground">
              {isAnyDown ? "Some Problems Detected" : "All Systems: Operational"}
            </h1>
          </div>
          <p className="text-xs text-muted-foreground uppercase font-mono">
            UptimeWing keeps watch so you don't have to keep refreshing.
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={() => setActiveTab("dashboard")}
            variant={activeTab === "dashboard" ? "default" : "outline"}
            className="rounded-none border-2 border-foreground font-black uppercase text-xs h-10 px-4"
          >
            My Watches
          </Button>
          <Button
            onClick={() => setActiveTab("flow")}
            variant={activeTab === "flow" ? "default" : "outline"}
            className="rounded-none border-2 border-foreground font-black uppercase text-xs h-10 px-4"
          >
            Flow Canvas
          </Button>
          <Button
            onClick={() => setWizardOpen(true)}
            className="rounded-none border-2 border-foreground bg-primary text-primary-foreground font-black uppercase text-xs h-10 px-6 shadow-xs"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Add Watch
          </Button>
        </div>
      </div>

      {activeTab === "flow" ? (
        <IntegrationsCanvas />
      ) : (
        <div className="space-y-6">
          {/* Watch Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Show Example Card when list is empty */}
            {sites.length === 0 && (
              <div className="border-4 border-dashed border-foreground p-6 bg-card text-foreground flex flex-col justify-between h-[180px] shadow-[4px_4px_0_0_#000] dark:shadow-[4px_4px_0_0_#fff] relative group">
                <div className="flex justify-between items-start">
                  <div>
                    <span className="text-xl mr-2">🌐</span>
                    <span className="font-black uppercase text-xs">{exampleCard.name}</span>
                  </div>
                  <span className="bg-green-500 text-white text-[9px] px-1.5 py-0.5 font-mono font-black uppercase border border-black animate-pulse">
                    EXAMPLE UP
                  </span>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-[10px] font-mono text-muted-foreground">
                    <span>Checked just now</span>
                    <span>{exampleCard.uptime_percentage}% Uptime</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <Sparkline />
                    <span className="text-[10px] font-black uppercase underline">Active Watch</span>
                  </div>
                </div>

                {/* Overlaid visual explanation */}
                <div className="absolute inset-0 bg-background/95 flex flex-col justify-center items-center p-4 text-center opacity-100 group-hover:opacity-0 transition-opacity pointer-events-none border-2 border-transparent">
                  <Sparkles className="h-5 w-5 text-yellow-500 mb-1 animate-bounce" />
                  <p className="text-[10px] font-black uppercase text-foreground leading-tight max-w-[200px]">
                    This is what a Watch looks like. Click "Add Watch" to add yours.
                  </p>
                </div>
              </div>
            )}

            {sites.map((site: any) => {
              const isUp = site.status === "up" || site.status === "active";
              return (
                <div
                  key={site.id}
                  className="border-4 border-foreground p-6 bg-card text-foreground flex flex-col justify-between h-[180px] shadow-[4px_4px_0_0_#000] dark:shadow-[4px_4px_0_0_#fff] relative group hover:-translate-x-0.5 hover:-translate-y-0.5 transition-all"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="text-xl mr-2">🌐</span>
                      <span className="font-black uppercase text-xs">{site.name}</span>
                    </div>
                    <span
                      className={`text-[9px] px-1.5 py-0.5 font-mono font-black uppercase border border-foreground ${
                        isUp ? "bg-green-500 text-white" : "bg-red-500 text-white animate-pulse"
                      }`}
                    >
                      {isUp ? "UP" : "DOWN"}
                    </span>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-[10px] font-mono text-muted-foreground">
                      <span>Checked {site.check_interval_minutes}m ago</span>
                      <span>{site.uptime_percentage ? `${Number(site.uptime_percentage).toFixed(2)}%` : "100%"} Uptime</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <Sparkline />
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => togglePauseMutation.mutate({ id: site.id, paused: site.is_active })}
                          className="h-6 rounded-none border border-foreground text-[8px] uppercase font-black px-2 bg-background text-foreground"
                        >
                          {site.is_active ? "Pause" : "Resume"}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Add Watch modal step flow wizard */}
      <AddWatchWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreated={handleCreated}
      />
    </div>
  );
}
